import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { useState } from "react";
import { FeedCardHalo } from "./FeedCardHalo";
import { FeedRssArticleEntry, FeedRssSectionItem } from "./types";

interface FeedRssClusterProps {
  item: FeedRssSectionItem;
  isActive: boolean;
}

const UTM_SUFFIX =
  "utm_source=globalcomix&utm_medium=feed&utm_campaign=suggestion_feed";

type SourceTheme = {
  accent: string;
  tintFrom: string;
  tintTo: string;
};

const SOURCE_THEMES: Record<string, SourceTheme> = {
  comicsbeat: {
    accent: "#FF7A2E",
    tintFrom: "rgba(60, 28, 12, 0.95)",
    tintTo: "rgba(18, 16, 28, 0.95)",
  },
  cbr: {
    accent: "#21D4FD",
    tintFrom: "rgba(12, 38, 60, 0.95)",
    tintTo: "rgba(18, 16, 28, 0.95)",
  },
  bleedingcool: {
    accent: "#FF3D7F",
    tintFrom: "rgba(58, 16, 38, 0.95)",
    tintTo: "rgba(18, 16, 28, 0.95)",
  },
  aipt: {
    accent: "#FFD23F",
    tintFrom: "rgba(54, 44, 14, 0.95)",
    tintTo: "rgba(18, 16, 28, 0.95)",
  },
  tcj: {
    accent: "#9BC4E2",
    tintFrom: "rgba(20, 32, 48, 0.95)",
    tintTo: "rgba(18, 16, 28, 0.95)",
  },
  brokenfrontier: {
    accent: "#B388FF",
    tintFrom: "rgba(40, 28, 64, 0.95)",
    tintTo: "rgba(18, 16, 28, 0.95)",
  },
  sktchd: {
    accent: "#4ADE80",
    tintFrom: "rgba(16, 40, 30, 0.95)",
    tintTo: "rgba(18, 16, 28, 0.95)",
  },
  smashpages: {
    accent: "#FFB454",
    tintFrom: "rgba(58, 36, 14, 0.95)",
    tintTo: "rgba(18, 16, 28, 0.95)",
  },
};

const DEFAULT_THEME: SourceTheme = {
  accent: "#FF8A3D",
  tintFrom: "rgba(38, 28, 56, 0.95)",
  tintTo: "rgba(18, 16, 28, 0.95)",
};

function themeFor(slug: string): SourceTheme {
  return SOURCE_THEMES[slug] ?? DEFAULT_THEME;
}

/**
 * "Comics news" cluster — one fullscreen slot composed of N article rows.
 * Each row is its own outbound link to the publication.
 */
export function FeedRssCluster({ item, isActive }: FeedRssClusterProps) {
  const heroSrc = item.articles.find((a) => a.image_url)?.image_url ?? null;

  return (
    <Box position="relative" w="100%" h="100%">
      <FeedCardHalo src={heroSrc} />
      <Box
        position="relative"
        zIndex={1}
        w="100%"
        h="100%"
        bg="rgba(14, 16, 24, 0.95)"
        borderRadius="2xl"
        border="1px solid rgba(255,255,255,0.06)"
        color="white"
        overflow="hidden"
        p={3}
      >
        <Flex direction="column" gap={3} h="100%">
          {item.articles.map((a) => (
            <ArticleRow key={a.rss_id} entry={a} isActive={isActive} />
          ))}
        </Flex>
      </Box>
    </Box>
  );
}

function ArticleRow({
  entry,
  isActive,
}: {
  entry: FeedRssArticleEntry;
  isActive: boolean;
}) {
  const href = appendUtm(entry.url);
  const timeAgo = formatTimeAgo(entry.published_time);
  const theme = themeFor(entry.source.slug);
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!entry.image_url && !imageFailed;

  return (
    <Flex
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      tabIndex={isActive ? 0 : -1}
      direction="row"
      flex="1"
      minH="0"
      borderRadius="xl"
      overflow="hidden"
      bg="rgba(20, 18, 30, 0.7)"
      border="1px solid rgba(255,255,255,0.05)"
      cursor="pointer"
      _hover={{ filter: "brightness(1.1)" }}
      transition="filter 160ms ease"
    >
      <Box
        position="relative"
        flexShrink={0}
        w="36%"
        maxW="160px"
        bg="black"
      >
        {showImage ? (
          <Image
            src={entry.image_url ?? ""}
            alt=""
            position="absolute"
            inset={0}
            w="100%"
            h="100%"
            objectFit="cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <Box
            position="absolute"
            inset={0}
            bgGradient={`linear(135deg, ${theme.tintFrom} 0%, ${theme.tintTo} 100%)`}
          />
        )}
      </Box>
      <Flex direction="column" flex="1" minW="0" px={3} py={2} gap={1}>
        <Flex align="center" gap={2}>
          {entry.source.logo_url ? (
            <Image
              src={entry.source.logo_url}
              alt=""
              boxSize="14px"
              borderRadius="sm"
              objectFit="contain"
            />
          ) : null}
          <Text
            fontSize="xs"
            fontWeight="extrabold"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color={theme.accent}
            noOfLines={1}
          >
            {entry.source.display_name}
          </Text>
          {timeAgo ? (
            <Text fontSize="xs" opacity={0.6} noOfLines={1}>
              · {timeAgo}
            </Text>
          ) : null}
        </Flex>
        <Text
          fontSize="md"
          fontWeight="bold"
          lineHeight="1.25"
          noOfLines={3}
        >
          {entry.title}
        </Text>
      </Flex>
    </Flex>
  );
}

function appendUtm(url: string): string {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${UTM_SUFFIX}`;
}

function formatTimeAgo(iso: string): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diffSec = Math.max(0, (Date.now() - t) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  return `${Math.floor(diffSec / 86400 / 7)}w ago`;
}
